import { Asset, Service } from '../../types/rest/assetCatalogTypes';
import React from 'react';
import { Button } from 'react-bootstrap';
import BzmSearchInput from '../../components/forms/BzmSearchInput/BzmSearchInput';
import ElevioTooltip from '../../components/overlays/helpTooltip/TitleHelpTooltip/ElevioTooltip';
import BzmIcon from '../../components/icons/BzmIcon';
import { AssetType } from '../../store/certificates/certificatesTypes';
import clsx from 'clsx';
import ServicePicker from '../../components/ui/servicePicker/ServicePicker';

interface CommonAssetHeaderProps {
  assetType: AssetType;
  assetDetail?: Asset;
  showAssetModal?: () => void;
  handleCreateNewAsset?: () => void;
  searchKeyword: string;
  selectedAssets: any[];
  clearTableSelection: () => void;
  setSearchKeyword: (value: string) => void;
  count: string;
  isDeleteDisabled: () => boolean;
  onDelete: (asset?: any) => void;
  middleComponent?: any;
  openBulkTagModal?: () => void;
  showAddButton?: boolean;
  isCompact?: boolean;
  showServicePickerLabel?: boolean;
  selectedService?: number;
  isMaintenanceEnabled?: boolean;
  handleChangeService?: (serviceId: number) => void;
}

interface AssetHeaderViewProps {
  title: string;
  btnAddTitle: string;
  description: string;
  searchPlaceholder: string;
  btnDeleteTitle: string;
  tagSelectedItems?: string;
  elevio: string;
}

const AssetsHeaderText: {
  [key: string]: AssetHeaderViewProps;
} = {
  ['CERTIFICATE']: {
    title: 'CERTIFICATES',
    btnAddTitle: 'Add New Certificate',
    description: 'List of certificates and keystores available in the workspace.',
    searchPlaceholder: 'Search Certificates...',
    btnDeleteTitle: 'Delete selected Certificates',
    elevio: 'certificates_help',
  },
  ['RECORDING']: {
    title: 'RECORDINGS',
    btnAddTitle: 'Add New Recording',
    description: 'List of Recordings available for Virtual Services creation.',
    searchPlaceholder: 'Search Recordings...',
    btnDeleteTitle: 'Delete selected Recordings',
    tagSelectedItems: 'Tag selected Recordings',
    elevio: 'message-recording-help',
  },
  ['MESSAGE']: {
    title: 'MESSAGES',
    btnAddTitle: 'Add New Message',
    description: 'List of recorded Messages.',
    searchPlaceholder: 'Search Messages...',
    btnDeleteTitle: 'Delete selected Messages',
    elevio: 'message-queue-help',
  },
};

const CommonAssetHeader = ({
  assetType,
  handleCreateNewAsset,
  searchKeyword,
  selectedAssets,
  clearTableSelection,
  setSearchKeyword,
  count,
  isDeleteDisabled,
  onDelete,
  showAddButton,
  middleComponent,
  openBulkTagModal,
  isCompact,
  showServicePickerLabel,
  selectedService,
  isMaintenanceEnabled,
  handleChangeService,
}: CommonAssetHeaderProps) => {
  return (
    <>
      {showServicePickerLabel === true && (
        <>
          <div className={'row flex-nowrap'}>
            <div className={'col d-flex justify-content-between'}>
              {/*<Form inline onSubmit={(e: any) => e.preventDefault()}>*/}

              <label className="mt-2 mr-3 font-weight-bold" htmlFor="serviceFilter" aria-describedby="servicePickerDiv">
                Service:
              </label>
              <div id="servicePickerDiv" className="mr-4 d-inline-block" style={{ minWidth: '200px' }}>
                <ServicePicker
                  preSelected={selectedService}
                  selectHandler={(sv: Service | null) =>
                    handleChangeService(sv === null ? 0 : sv.id === undefined ? 0 : sv.id)
                  }
                  isSelectAll={true}
                  isCreateEnabled={true}
                  id="serviceFilter"
                  isMaintenanceEnabled={isMaintenanceEnabled}
                  isCloneEnabled={true}
                />
              </div>
              <div id="transactionTypeFilter" className="mr-4 d-inline-block"></div>
              <div id="additional" className="ml-auto"></div>
            </div>
            <div className={'col-auto'}>
              <div className={clsx('float-right position-relative', !isCompact && 'mr-5')} style={{ top: 10 }}>
                <BzmSearchInput
                  value={searchKeyword}
                  placeholder={AssetsHeaderText[assetType].searchPlaceholder}
                  onChange={value => {
                    if (selectedAssets.length !== 0) {
                      clearTableSelection();
                    }
                    setSearchKeyword(value);
                  }}
                />
              </div>
            </div>
          </div>
          <hr />
        </>
      )}

      {showAddButton !== false && (
        <Button
          id={'addNewAssetId'}
          title={AssetsHeaderText[assetType].btnAddTitle}
          variant="primary"
          className="float-right btn btn-primary btn-circular btn-jb btn btn-primary"
          onClick={handleCreateNewAsset}
        >
          <span className="icon-bzm-add" aria-hidden="true"></span>
        </Button>
      )}
      {middleComponent !== undefined && middleComponent}
      {showServicePickerLabel !== true && (
        <div className={clsx('float-right position-relative', !isCompact && 'mr-5')} style={{ top: 10 }}>
          <BzmSearchInput
            value={searchKeyword}
            placeholder={AssetsHeaderText[assetType].searchPlaceholder}
            onChange={value => {
              if (selectedAssets.length !== 0) {
                clearTableSelection();
              }
              setSearchKeyword(value);
            }}
          />
        </div>
      )}
      <h2 className="mb-3">
        <span className="mr-2" id="assetTitleId" title={`${AssetsHeaderText[assetType].title}`}>
          {AssetsHeaderText[assetType].title}
        </span>
        {count}
        <ElevioTooltip elevioId={AssetsHeaderText[assetType].elevio} className={'ml-3'} />
        <Button
          disabled={isDeleteDisabled()}
          title={AssetsHeaderText[assetType].btnDeleteTitle}
          id={`qa-blk-asset-delete`}
          variant={'link'}
          className={'btn btn-no-hover p-0 mx-2'}
          onClick={() => onDelete()}
        >
          <BzmIcon variant={'icon-bzm-delete'} className={'icon-sm'} />
        </Button>
        {openBulkTagModal && (
          <button
            id={`qa-blk-recording-tag`}
            className="btn btn-link btn-no-hover p-0 mx-2"
            title={AssetsHeaderText[assetType].tagSelectedItems}
            onClick={openBulkTagModal}
            disabled={isDeleteDisabled()}
          >
            <BzmIcon variant={'icon-bzm-tag'} className={'icon-sm'} />
            <span className="sr-only">title={AssetsHeaderText[assetType].tagSelectedItems}</span>
          </button>
        )}
      </h2>

      <p>{AssetsHeaderText[assetType].description}</p>
    </>
  );
};

export default CommonAssetHeader;
